import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Twitter,
  Instagram,
  Github,
  Youtube,
  Apple,
  Play,
  Globe,
  ArrowRight,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ENV } from "@/config/env";

const COLUMNS = [
  {
    title: "Discover",
    links: [
      { label: "Explore", to: "/explore" },
      { label: "Live map", to: "/map" },
      { label: "Upcoming", to: "/explore" },
      { label: "Replays", to: "/explore" },
    ],
  },
  {
    title: "Categories",
    links: [
      { label: "Music", to: "/explore?category=Music" },
      { label: "Nightlife", to: "/explore?category=Nightlife" },
      { label: "Food", to: "/explore?category=Food" },
      { label: "Tech", to: "/explore?category=Tech" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/" },
      { label: "Creators", to: "/" },
      { label: "Careers", to: "/" },
      { label: "Press", to: "/" },
    ],
  },
  {
    title: "Account",
    links: [
      { label: "Log in", to: "/login" },
      { label: "Sign up", to: "/register" },
      { label: "Help center", to: "/" },
      { label: "Contact", to: "/" },
    ],
  },
];

const SOCIALS = [Twitter, Instagram, Youtube, Github];

export function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    toast.success("You're on the list (demo)", {
      description: "Newsletter signup is a placeholder in this prototype.",
    });
    setEmail("");
  };

  return (
    <footer data-testid="footer" className="border-t border-border bg-[#0A1526] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Newsletter */}
        <div className="grid gap-8 border-b border-white/10 py-14 md:grid-cols-2 md:items-center">
          <div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
              Never miss a moment going live.
            </h3>
            <p className="mt-2 max-w-md text-white/60">
              Get a weekly digest of the best streams and events near you.
            </p>
          </div>
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="flex w-full items-center gap-2 rounded-full border border-white/15 bg-white/5 p-1.5 md:ml-auto md:max-w-md"
          >
            <input
              data-testid="newsletter-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full bg-transparent px-4 py-2.5 text-sm text-white outline-none placeholder:text-white/40"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
            >
              Subscribe <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>

        {/* Link columns */}
        <div className="grid gap-10 py-14 md:grid-cols-6">
          <div className="md:col-span-2">
            <Logo size={40} wordmarkColor="text-white" />
            <p className="mt-4 max-w-xs text-sm text-white/60">
              Discover live streams and real-world events happening around you — all on one map.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((Icon, i) => (
                <button
                  key={i}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-colors hover:border-brand hover:text-white"
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="mb-4 font-heading text-sm font-bold text-white">{col.title}</h4>
              <ul className="space-y-2.5 text-sm text-white/60">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="transition-colors hover:text-white">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* App badges */}
        <div className="flex flex-col items-start gap-4 border-t border-white/10 py-8 sm:flex-row sm:items-center">
          <span className="text-sm text-white/50">Get the mobile app</span>
          <div className="flex gap-3">
            <span className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white">
              <Apple className="h-5 w-5" /> App Store
            </span>
            <span className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white">
              <Play className="h-5 w-5" /> Google Play
            </span>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-start justify-between gap-4 border-t border-white/10 py-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-4">
            <span>© {new Date().getFullYear()} {ENV.APP_NAME}. All rights reserved.</span>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span className="flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5" /> English (US)
            </span>
          </div>
          <span className="rounded-full bg-brand/15 px-3 py-1 font-medium text-brand">
            Live data connected · Premium tickets & replays are demo
          </span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
